//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { hasBVImportLabel } from '@legacy-components/common/universal-image-actions/checkItemRightsStatus';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import Cuid from '@models/Cuid';
import { RightsCreationSource } from '@models/RightsCreationSource';
import { SocialNetwork } from '@models/SocialNetwork';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { UIANotSupportedRights } from '@uia/rights/UIANotSupportedRights';
import { UIARequestStatus } from '@uia/rights/UIARequestStatus';
import { UIASupportedRightsRequest } from '@uia/rights/UIASupportedRightsRequest';
import { isDefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

const SUPPORTED_NETWORKS = [
  SocialNetwork.Instagram,
  SocialNetwork.TikTok,
  SocialNetwork.Twitter,
  SocialNetwork.Bazaarvoice,
];

const APPROVED_SOURCES = [
  RightsCreationSource.RelationshipClientToClient,
  RightsCreationSource.RelationshipInfluencerToClient,
  RightsCreationSource.SkippedRightsRequest,
];

export interface UIARightsProps {
  approvalPhrase?: string;
  cuid: Cuid;
  onCloseModal: () => void;
  requestType: RightsRequestOptions;
  rightsRequestText?: string;
  setApprovalPhrase: (value?: string) => void;
  setRequestType: (selectedValue: RightsRequestOptions) => void;
  setRightsRequestText: (value?: string) => void;
}

export const UIARightsRequestBody = ({
  approvalPhrase,
  cuid,
  onCloseModal,
  requestType,
  rightsRequestText,
  setApprovalPhrase,
  setRequestType,
  setRightsRequestText,
}: UIARightsProps) => {
  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  if (isLoadingMetadata || isErrorMetadata) {
    return null;
  }

  const socialNetwork = metadata.socialNetwork ?? SocialNetwork.Unknown;
  const creationSource = metadata.rightsPermissions?.creationSource;
  const isBVImport = hasBVImportLabel(metadata);

  const isSupportedNetwork = (() => {
    if (SUPPORTED_NETWORKS.includes(socialNetwork)) {
      return socialNetwork === SocialNetwork.Instagram
        ? isDefined(metadata.originalUrl)
        : true;
    }
    return false;
  })();

  const rightsRequestStatus = metadata.rightsRequestStatus.status;
  if (
    rightsRequestStatus === RIGHTS_STATUS.BRAND_OWNED ||
    (creationSource && APPROVED_SOURCES.includes(creationSource))
  ) {
    return (
      <Wrapper>
        <UIARequestStatus
          isBVImport={isBVImport}
          onCloseModal={onCloseModal}
          source={creationSource}
          status={rightsRequestStatus}
        />
      </Wrapper>
    );
  }

  if (isSupportedNetwork) {
    return (
      <Wrapper>
        <UIASupportedRightsRequest
          approvalPhrase={approvalPhrase}
          cuid={cuid}
          requestType={requestType}
          rightsRequestText={rightsRequestText}
          setApprovalPhrase={setApprovalPhrase}
          setRequestType={setRequestType}
          setRightsRequestText={setRightsRequestText}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <UIANotSupportedRights socialNetwork={socialNetwork} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
`;
